/**
 * Canonical URL of the `MedplumHealthGorillaPatient` profile. Setting this on `Patient.meta.profile`
 * tells the Medplum server to validate the resource against the Health Gorilla profile on every
 * write — required for any patient we'll submit for HG-backed lab orders.
 *
 * @see https://medplum.com/profiles/integrations/health-gorilla/StructureDefinition/MedplumHealthGorillaPatient
 */
export const HEALTH_GORILLA_PATIENT_PROFILE_URL =
  'https://medplum.com/profiles/integrations/health-gorilla/StructureDefinition/MedplumHealthGorillaPatient';

/**
 * Canonical URL of the `MedplumHealthGorillaOrder` profile. Setting this on
 * `ServiceRequest.meta.profile` tags a request as a Health-Gorilla-bound lab order. Used both
 * for write-time profile validation and as a search filter (`_profile=...`) when listing only
 * lab orders we created via the HG integration — distinguishing them from any `ServiceRequest`
 * resources that might exist for other reasons.
 *
 * @see https://medplum.com/profiles/integrations/health-gorilla/StructureDefinition/MedplumHealthGorillaOrder
 */
export const HEALTH_GORILLA_ORDER_PROFILE_URL =
  'https://medplum.com/profiles/integrations/health-gorilla/StructureDefinition/MedplumHealthGorillaOrder';

const LIZA_PROFILE_BASE = 'https://liza.health/fhir/StructureDefinition';

/**
 * Canonical URLs of Liza's Task profiles — one per model that shares the Task resource type
 * (2026-08-31 decision). Built and deployed by `medplum-config`; set on `Task.meta.profile` for
 * write-time validation and used as `_profile=` search filters, including access-policy allowlists.
 * `PHARMACY_MESSAGE` is descriptive only and is never stamped on a resource.
 *
 * No URL here may be a substring of another: Medplum's in-memory matcher evaluates `_profile` with
 * `includes`, so a prefix would make a policy criterion admit the wrong profile. Hence
 * `LizaOneOffCarePlanItem`, not `LizaCarePlanItemOneOff`. Pinned by a medplum-config test.
 */
export const LIZA_TASK_PROFILES = {
  CARE_PLAN_ITEM: `${LIZA_PROFILE_BASE}/LizaCarePlanItem`,
  CARE_PLAN_ITEM_ONE_OFF: `${LIZA_PROFILE_BASE}/LizaOneOffCarePlanItem`,
  CARE_PLAN_ITEM_RECURRING: `${LIZA_PROFILE_BASE}/LizaRecurringCarePlanItem`,
  CARE_PLAN_ITEM_AS_NEEDED: `${LIZA_PROFILE_BASE}/LizaAsNeededCarePlanItem`,
  CARE_TEAM_TASK: `${LIZA_PROFILE_BASE}/LizaCareTeamTask`,
  TASK_OCCURRENCE: `${LIZA_PROFILE_BASE}/LizaTaskOccurrence`,
  SUGGESTED_ACTION: `${LIZA_PROFILE_BASE}/LizaSuggestedAction`,
  CHAT_ESCALATION: `${LIZA_PROFILE_BASE}/LizaChatEscalation`,
  SURVEY_REVIEW: `${LIZA_PROFILE_BASE}/LizaSurveyReview`,
  PHARMACY_MESSAGE: `${LIZA_PROFILE_BASE}/LizaPharmacyMessage`,
} as const;

/**
 * Canonical URLs of Liza's Communication profiles — one per concept that shares the Communication
 * resource type (2026-09-04 decision). Stamped on threads, messages and faxes alike; the two
 * shared-chat profiles are the patient access policy's allowlist and everything else is
 * practice-internal. The same substring rule as {@link LIZA_TASK_PROFILES} applies.
 */
export const LIZA_COMMUNICATION_PROFILES = {
  SHARED_CHAT_THREAD: `${LIZA_PROFILE_BASE}/LizaSharedChatThread`,
  SHARED_CHAT_MESSAGE: `${LIZA_PROFILE_BASE}/LizaSharedChatMessage`,
  PROVIDER_CHAT_THREAD: `${LIZA_PROFILE_BASE}/LizaProviderChatThread`,
  PROVIDER_CHAT_MESSAGE: `${LIZA_PROFILE_BASE}/LizaProviderChatMessage`,
  INBOUND_FAX: `${LIZA_PROFILE_BASE}/LizaInboundFax`,
  OUTBOUND_FAX: `${LIZA_PROFILE_BASE}/LizaOutboundFax`,
} as const;
